/**
 * ProposalCard — THE text-edit proposal surface for the chat thread.
 *
 * One presentation for every "Asthra proposes replacement text →
 * writer approves or rejects" moment, regardless of who initiated it:
 *   - EditProposalMessage (writer-initiated highlight edit) adapts
 *     the /api/selection-edit proposal onto this card.
 *   - WriteApprovalMessage (agent-initiated doc_write_section gate)
 *     adapts the WebSocket write_approval request onto this card.
 *
 * The card renders as an assistant-style bubble: label row (Asthra ·
 * time + intent-style chip), a mono header naming the target, the
 * proposed content as MARKDOWN (tables/headings preview the way they
 * land in Word), a collapsed "show original text" disclosure, an
 * optional warning line, an optional extra body slot (e.g. the
 * write-approval edit-before-approving textarea), and Approve/Reject
 * buttons directly below the output.
 *
 * SRP: pure presentation. Callers own all state machines and apply
 * logic; the card never talks to Word, the bridge, or the backend.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkSubSup } from "../../services/remarkSubSup";
import { brandColors, fonts, inkColors, statusColors } from "../tokens";
import AsthraBubbleSurface from "./AsthraBubbleSurface";
import { formatTime } from "./MessageBubble";
import { assistantMarkdownRules } from "./markdownStyles";

export interface ProposalCardProps {
  /** Chip text next to "Asthra · HH:MM" (e.g. "Edit highlight",
   *  "Write approval"). */
  chipText: string;
  /** One-line mono header naming the target + instruction/reason. */
  header: string;
  /** Proposed content, rendered as markdown. Ignored when ``previewNode``
   *  is provided. */
  previewMarkdown: string;
  /** Custom preview element rendered in place of the markdown block —
   *  e.g. the table-edit snippet diff with changed cells highlighted.
   *  When set, ``previewMarkdown`` is not rendered. */
  previewNode?: React.ReactNode;
  /** The before-text shown behind the "show original text" toggle.
   *  Omit to hide the disclosure entirely. */
  originalText?: string | null;
  /** Optional warning line (ambiguity / drift / disconnect). */
  warning?: string | null;
  /** Optional slot rendered between the preview and the actions —
   *  e.g. the write-approval edit-before-approving textarea. */
  extraBody?: React.ReactNode;
  /** Optional meta chips row under the header (e.g. §9.2 · Insert). */
  metaChips?: readonly string[];
  /** True while the decision is being applied — disables both
   *  buttons and swaps the approve label for ``busyLabel``. */
  busy?: boolean;
  /** True when the card can't currently be decided (e.g. bridge
   *  disconnected) — disables both buttons without the busy label. */
  disabled?: boolean;
  approveLabel?: string;
  busyLabel?: string;
  ariaLabel: string;
  onApprove: () => void;
  onReject: () => void;
  /** When provided, a "Revise" action appears alongside Approve/Reject.
   *  Choosing it opens an inline input; submitting calls ``onRevise`` with
   *  the writer's revision instruction (the caller re-runs the rewrite
   *  against the current proposal + this instruction). Omit to hide the
   *  affordance entirely (write-approval / ad-hoc cards stay Approve/Reject). */
  onRevise?: (revisionInstruction: string) => void;
  /** Optional status line shown in place of the actions while ``busy`` — e.g.
   *  the live "Revising…" progress while a revision re-run is in flight. */
  statusLine?: string | null;
}

const useStyles = makeStyles({
  outer: {
    alignSelf: "flex-start",
    maxWidth: "95%",
  },
  bubble: {
    padding: "8px 12px",
    fontSize: "12.5px",
    wordBreak: "break-word",
  },
  intentChip: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    color: brandColors.mid,
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.glow}`,
    borderRadius: "8px",
    padding: "0px 6px",
    lineHeight: "13px",
    whiteSpace: "nowrap",
    marginLeft: "5px",
  },
  header: {
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: inkColors.ink3,
    marginBottom: "6px",
    letterSpacing: "0.2px",
  },
  metaRow: {
    display: "flex",
    columnGap: "6px",
    rowGap: "4px",
    flexWrap: "wrap",
    marginBottom: "6px",
  },
  metaChip: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    fontWeight: 600,
    color: inkColors.ink2,
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.glow}`,
    borderRadius: "999px",
    padding: "1px 8px",
    whiteSpace: "nowrap",
  },
  markdown: {
    ...assistantMarkdownRules,
  },
  originalToggle: {
    marginTop: "6px",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    cursor: "pointer",
    textDecoration: "underline",
  },
  originalPane: {
    marginTop: "4px",
    fontSize: "11.5px",
    lineHeight: 1.45,
    color: inkColors.ink2,
    whiteSpace: "pre-wrap",
    maxHeight: "140px",
    overflowY: "auto",
    padding: "6px 8px",
    borderRadius: "4px",
    backgroundColor: "#FFF5F5",
    border: "1px solid #F3C4C4",
  },
  warning: {
    marginTop: "6px",
    fontSize: "11px",
    color: statusColors.warn,
  },
  actions: {
    display: "flex",
    columnGap: "8px",
    justifyContent: "flex-end",
    marginTop: "8px",
  },
  statusLine: {
    marginTop: "8px",
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    fontStyle: "italic",
  },
  revisePanel: {
    marginTop: "8px",
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  reviseTextarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "52px",
    resize: "vertical",
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: 1.45,
    color: inkColors.ink2,
    padding: "6px 8px",
    borderRadius: "4px",
    border: `1px solid ${brandColors.glow}`,
    ":focus": { outline: "none", border: `1px solid ${brandColors.mid}` },
  },
  reviseActions: {
    display: "flex",
    columnGap: "8px",
    justifyContent: "flex-end",
  },
  reviseBtn: {
    borderRadius: "4px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${brandColors.mid}`,
    color: brandColors.mid,
    marginRight: "auto",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  reviseSendBtn: {
    border: "none",
    borderRadius: "4px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: brandColors.mid,
    color: "#fff",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  approveBtn: {
    border: "none",
    borderRadius: "4px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: statusColors.cool,
    color: "#fff",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  rejectBtn: {
    borderRadius: "4px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${inkColors.ink4}`,
    color: inkColors.ink2,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
});

export const ProposalCard: React.FC<ProposalCardProps> = ({
  chipText,
  header,
  previewMarkdown,
  previewNode = null,
  originalText = null,
  warning = null,
  extraBody = null,
  metaChips,
  busy = false,
  disabled = false,
  approveLabel = "Approve",
  busyLabel = "Applying…",
  ariaLabel,
  onApprove,
  onReject,
  onRevise,
  statusLine = null,
}) => {
  const styles = useStyles();
  const [showOriginal, setShowOriginal] = useState(false);
  // Inline revise input: open reveals a textarea in place of the actions.
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseText, setReviseText] = useState("");
  const time = formatTime(Date.now());
  const blocked = busy || disabled;

  const submitRevision = () => {
    const text = reviseText.trim();
    if (!text || !onRevise) return;
    onRevise(text);
    // Caller flips the card to busy/"revising"; reset the local input so a
    // later revise starts clean.
    setReviseText("");
    setReviseOpen(false);
  };

  const label = (
    <>
      {`Asthra · ${time}`}
      <span className={styles.intentChip} aria-label={`Intent: ${chipText}`}>
        {chipText}
      </span>
    </>
  );

  return (
    <AsthraBubbleSurface
      label={label}
      className={styles.outer}
      // biome-ignore lint/a11y/useSemanticElements: AsthraBubbleSurface forwards role to its inner div
      bubbleClassName={styles.bubble}
      role="region"
      ariaLabel={ariaLabel}
    >
      <div className={styles.header}>{header}</div>
      {metaChips && metaChips.length > 0 ? (
        <div className={styles.metaRow}>
          {metaChips.map((chip) => (
            <span key={chip} className={styles.metaChip}>
              {chip}
            </span>
          ))}
        </div>
      ) : null}
      {previewNode ? (
        previewNode
      ) : (
        <div className={styles.markdown}>
          <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSubSup]}>
            {previewMarkdown}
          </ReactMarkdown>
        </div>
      )}
      {originalText ? (
        <>
          <button
            type="button"
            className={styles.originalToggle}
            onClick={() => setShowOriginal((s) => !s)}
            aria-expanded={showOriginal}
          >
            {showOriginal ? "hide original text" : "show original text"}
          </button>
          {showOriginal && <div className={styles.originalPane}>{originalText}</div>}
        </>
      ) : null}
      {extraBody}
      {warning ? <div className={styles.warning}>{warning}</div> : null}
      {busy && statusLine ? (
        <div className={styles.statusLine} aria-live="polite">
          {statusLine}
        </div>
      ) : onRevise && reviseOpen && !blocked ? (
        <div className={styles.revisePanel}>
          <textarea
            className={styles.reviseTextarea}
            value={reviseText}
            onChange={(e) => setReviseText(e.target.value)}
            placeholder="What should change? (e.g. make it more concise, cite the source, keep the number)"
            aria-label="Revision instruction"
            // Cmd/Ctrl+Enter submits; plain Enter allows multi-line.
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submitRevision();
              }
            }}
          />
          <div className={styles.reviseActions}>
            <button
              type="button"
              className={styles.rejectBtn}
              onClick={() => {
                setReviseOpen(false);
                setReviseText("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.reviseSendBtn}
              onClick={submitRevision}
              disabled={!reviseText.trim()}
            >
              Send revision
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          {onRevise ? (
            <button
              type="button"
              className={styles.reviseBtn}
              onClick={() => setReviseOpen(true)}
              disabled={blocked}
            >
              Revise
            </button>
          ) : null}
          <button type="button" className={styles.rejectBtn} onClick={onReject} disabled={blocked}>
            Reject
          </button>
          <button type="button" className={styles.approveBtn} onClick={onApprove} disabled={blocked}>
            {busy ? busyLabel : approveLabel}
          </button>
        </div>
      )}
    </AsthraBubbleSurface>
  );
};

export default ProposalCard;
